import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const TrackingDocs = ({ open, onClose, leadId }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [copiedField, setCopiedField] = useState(null);

  const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin + '/api';
  const trackingPixelUrl = `${apiBaseUrl}/track/pixel?lid=${leadId}`;
  const trackingEventUrl = `${apiBaseUrl}/track/event`;

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const CodeBlock = ({ code, language, copyField }) => (
    <Paper
      sx={{
        p: 2,
        bgcolor: 'grey.900',
        color: 'grey.100',
        borderRadius: 1,
        position: 'relative',
        overflow: 'auto',
      }}
    >
      <IconButton
        size="small"
        onClick={() => handleCopy(code, copyField)}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'grey.400',
          '&:hover': { color: 'white' },
        }}
      >
        {copiedField === copyField ? (
          <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
        ) : (
          <CopyIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>
      <Typography
        component="pre"
        sx={{
          m: 0,
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {code}
      </Typography>
    </Paper>
  );

  const htmlPixelCode = `<!-- Simple Tracking Pixel (no location) - Add before </body> -->
<img src="${trackingPixelUrl}"
     style="display:none"
     alt="" />`;

  const htmlScriptCode = `<!-- Tracking Script with Location - Add before </body> -->
<script>
(function() {
  // Fetch visitor location from freeipapi.com
  fetch('https://free.freeipapi.com/api/json/')
    .then(r => r.json())
    .then(geo => {
      // Send tracking data with location
      fetch('${trackingEventUrl}', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lid: '${leadId}',
          ref: window.location.href,
          path: window.location.pathname,
          sid: sessionStorage.getItem('_tid') || (sessionStorage._tid = Math.random().toString(36).substr(2)),
          location: {
            country: geo.countryName,
            countryCode: geo.countryCode,
            regionName: geo.regionName,
            city: geo.cityName,
            zip: geo.zipCode,
            lat: geo.latitude,
            lon: geo.longitude
          }
        })
      });
    });
})();
</script>`;

  const reactCode = `// Add to your React component
import { useEffect } from 'react';

const TrackingWithLocation = () => {
  useEffect(() => {
    // Fetch location from freeipapi.com then send tracking
    fetch('https://free.freeipapi.com/api/json/')
      .then(r => r.json())
      .then(geo => {
        fetch('${trackingEventUrl}', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lid: '${leadId}',
            ref: window.location.href,
            path: window.location.pathname,
            location: {
              country: geo.countryName,
              countryCode: geo.countryCode,
              city: geo.cityName,
              regionName: geo.regionName,
              lat: geo.latitude,
              lon: geo.longitude
            }
          })
        });
      }).catch(() => {});
  }, []);

  return null;
};`;

  const nextjsCode = `// pages/_app.js or app/layout.js
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

export default function RootLayout({ children }) {
  const pathname = usePathname();

  useEffect(() => {
    // Fetch location then send tracking
    fetch('https://free.freeipapi.com/api/json/')
      .then(r => r.json())
      .then(geo => {
        fetch('${trackingEventUrl}', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lid: '${leadId}',
            ref: window.location.href,
            path: pathname,
            location: {
              country: geo.countryName,
              city: geo.cityName,
              regionName: geo.regionName,
              lat: geo.latitude,
              lon: geo.longitude
            }
          })
        });
      }).catch(() => {});
  }, [pathname]);

  return children;
}`;

  const phpCode = `<!-- Add to your PHP page footer for tracking with location -->
<script>
fetch('https://free.freeipapi.com/api/json/')
  .then(r => r.json())
  .then(geo => {
    fetch('${trackingEventUrl}', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lid: '${leadId}',
        ref: window.location.href,
        path: window.location.pathname,
        location: {
          country: geo.countryName,
          city: geo.cityName,
          regionName: geo.regionName,
          lat: geo.latitude,
          lon: geo.longitude
        }
      })
    });
  });
</script>`;

  const wordpressCode = `<?php
// Add to your theme's functions.php

function add_tracking_script() {
    ?>
    <script>
    fetch('https://free.freeipapi.com/api/json/')
      .then(r => r.json())
      .then(geo => {
        fetch('${trackingEventUrl}', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lid: '${leadId}',
            ref: window.location.href,
            path: window.location.pathname,
            location: {
              country: geo.countryName,
              city: geo.cityName,
              regionName: geo.regionName,
              lat: geo.latitude,
              lon: geo.longitude
            }
          })
        });
      });
    </script>
    <?php
}
add_action('wp_footer', 'add_tracking_script');
?>`;

  const webflowCode = `<!-- Add to Webflow: Settings > Custom Code > Footer Code -->

<script>
fetch('https://free.freeipapi.com/api/json/')
  .then(r => r.json())
  .then(geo => {
    fetch('${trackingEventUrl}', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lid: '${leadId}',
        ref: window.location.href,
        path: window.location.pathname,
        location: {
          country: geo.countryName,
          city: geo.cityName,
          regionName: geo.regionName,
          lat: geo.latitude,
          lon: geo.longitude
        }
      })
    });
  });
</script>`;

  const curlCode = `# Test the tracking endpoint
curl -X POST ${trackingEventUrl} \\
  -H "Content-Type: application/json" \\
  -d '{
    "lid": "${leadId}",
    "ref": "https://your-pitch-site.com/demo",
    "path": "/demo"
  }'`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          Pitch Site Tracking Setup
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Quick Copy Section */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Quick Copy - Tracking Pixel URL</Typography>
          <TextField
            fullWidth
            size="small"
            value={trackingPixelUrl}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(trackingPixelUrl, 'url')}
                  >
                    {copiedField === 'url' ? (
                      <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : (
                      <CopyIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mt: 1, bgcolor: 'white' }}
          />
        </Alert>

        {/* IP Geolocation Info */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            IP Geolocation Service
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Location is fetched client-side using <strong>freeipapi.com</strong> (free, no API key required).
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Free tier: 60 requests/minute, no signup required
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Fields: countryName, countryCode, cityName, regionName, latitude, longitude, zipCode
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Endpoint: <code>https://free.freeipapi.com/api/json/</code>
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Docs: <a href="https://freeipapi.com/docs" target="_blank" rel="noopener noreferrer">https://freeipapi.com/docs</a>
            </Typography>
          </Box>
        </Paper>

        {/* Integration Examples */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Integration Examples
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="HTML Pixel" />
          <Tab label="HTML Script" />
          <Tab label="React" />
          <Tab label="Next.js" />
          <Tab label="Webflow" />
          <Tab label="WordPress" />
          <Tab label="PHP" />
          <Tab label="cURL" />
        </Tabs>

        {activeTab === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              The simplest method - just add an invisible image tag to your page.
            </Typography>
            <CodeBlock code={htmlPixelCode} copyField="html-pixel" />
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              JavaScript tracking with session support for tracking unique visitors.
            </Typography>
            <CodeBlock code={htmlScriptCode} copyField="html-script" />
          </Box>
        )}

        {activeTab === 2 && (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add tracking to your React application.
            </Typography>
            <CodeBlock code={reactCode} copyField="react" />
          </Box>
        )}

        {activeTab === 3 && (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Track page views in Next.js with route change support.
            </Typography>
            <CodeBlock code={nextjsCode} copyField="nextjs" />
          </Box>
        )}

        {activeTab === 4 && (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add tracking to your Webflow site via custom code settings.
            </Typography>
            <CodeBlock code={webflowCode} copyField="webflow" />
          </Box>
        )}

        {activeTab === 5 && (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add tracking to your WordPress theme.
            </Typography>
            <CodeBlock code={wordpressCode} copyField="wordpress" />
          </Box>
        )}

        {activeTab === 6 && (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Server-side tracking with PHP.
            </Typography>
            <CodeBlock code={phpCode} copyField="php" />
          </Box>
        )}

        {activeTab === 7 && (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Test the tracking endpoint from the command line.
            </Typography>
            <CodeBlock code={curlCode} copyField="curl" />
          </Box>
        )}

        {/* How It Works */}
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom>
            How It Works
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Add the tracking pixel or script to your pitch site
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              When someone visits the page, a request is sent with the page URL
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              The visitor's IP is looked up via ip-api.com for location data
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Visit is recorded and matched to leads based on the domain in their source/notes
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              View visits in the Lead Detail page under "Pitch Site Visits"
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={copiedField === 'pixel-btn' ? <CheckIcon /> : <CopyIcon />}
          onClick={() => handleCopy(htmlPixelCode, 'pixel-btn')}
        >
          {copiedField === 'pixel-btn' ? 'Copied!' : 'Copy Pixel Code'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrackingDocs;
